import { useEffect, useState, type ReactNode } from "react";
import type { ClientModelForName, CreateFields, Product, Purchase } from "@tellescope/types-client";
import {
  ClipboardCheck,
  Fullscreen,
  Minimize,
  ListPlus,
  FilePlus2,
  CloudUpload,
  Banknote,
  CreditCard,
} from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../breadcrumb";
import { Button } from "../button";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "../Dialog";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "../sidebar";

// Import all content components
import {
  InformationContent,
  TicketsContent,
  EventsContent,
  EligibilityContent,
  EligibilitySubmissionContent,
  FileUploadContent,
  FilesContent,
  FormsContent,
  FormResponsesContent,
  FormTemplateSelectionContent,
  FormQuestionnaireContent,
  ResponseDetailContent,
  MedicationsContent,
  PaymentContent,
  LogPurchaseContent,
} from "./atoms";
import { MedicationUpload } from "../molecules";

// Import constants and types
import { NAVIGATION_DATA } from "./constants";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip";
import { ChargePurchaseContent } from "./atoms/ChargePurchaseContent";
import { EligibilityResult } from "./types";
import { cn } from "@tellescope/utils";
import { useTranslation } from "react-i18next";

// Mock data for forms and responses
const initialForms = [
  {
    id: "1",
    title: "Patient Intake Form",
    responses: "47",
    date: "12/15/2024",
  },
  {
    id: "2",
    title: "Health Assessment Questionnaire",
    responses: "23",
    date: "12/10/2024",
  },
  {
    id: "3",
    title: "Medical History Survey",
    responses: "89",
    date: "12/08/2024",
  },
  {
    id: "4",
    title: "Symptom Tracker Form",
    responses: "12",
    date: "12/05/2024",
  },
  {
    id: "5",
    title: "Treatment Consent Form",
    responses: "156",
    date: "12/01/2024",
  },
];

// Mock form responses data
const mockFormResponses = {
  "1": [
    // Patient Intake Form
    {
      id: "r1",
      responderName: "Sarah Johnson",
      isAdmin: false,
      date: "12/15/2024 2:30 PM",
      status: "completed" as const,
    },
    {
      id: "r2",
      responderName: "Michael Chen",
      isAdmin: false,
      date: "12/14/2024 4:15 PM",
      status: "completed" as const,
    },
    {
      id: "r3",
      responderName: "Dr. Amanda Smith",
      isAdmin: true,
      date: "12/13/2024 11:00 AM",
      status: "completed" as const,
    },
    {
      id: "r4",
      responderName: "Emily Rodriguez",
      isAdmin: false,
      date: "12/12/2024 9:45 AM",
      status: "draft" as const,
    },
  ],
  "2": [
    // Health Assessment Questionnaire
    {
      id: "r5",
      responderName: "Robert Williams",
      isAdmin: false,
      date: "12/10/2024 3:20 PM",
      status: "completed" as const,
    },
    {
      id: "r6",
      responderName: "Lisa Anderson",
      isAdmin: false,
      date: "12/09/2024 1:30 PM",
      status: "completed" as const,
    },
    {
      id: "r7",
      responderName: "Nurse Karen Thompson",
      isAdmin: true,
      date: "12/08/2024 10:15 AM",
      status: "completed" as const,
    },
  ],
  "3": [
    // Medical History Survey
    {
      id: "r8",
      responderName: "David Martinez",
      isAdmin: false,
      date: "12/08/2024 5:00 PM",
      status: "completed" as const,
    },
    {
      id: "r9",
      responderName: "Jennifer Taylor",
      isAdmin: false,
      date: "12/07/2024 2:45 PM",
      status: "completed" as const,
    },
  ],
  "form-response-1": [
    // Patient Intake Form (Timeline version) - Multiple responses for timeline demo
    {
      id: "tr1",
      responderName: "Maria Rodriguez",
      isAdmin: false,
      date: "12/15/2024 9:30 AM",
      status: "completed" as const,
    },
    {
      id: "tr2",
      responderName: "Dr. James Wilson",
      isAdmin: true,
      date: "12/14/2024 3:45 PM",
      status: "completed" as const,
    },
    {
      id: "tr3",
      responderName: "Alex Thompson",
      isAdmin: false,
      date: "12/13/2024 1:20 PM",
      status: "completed" as const,
    },
    {
      id: "tr4",
      responderName: "Nurse Sarah Davis",
      isAdmin: true,
      date: "12/12/2024 11:15 AM",
      status: "completed" as const,
    },
    {
      id: "tr5",
      responderName: "Jessica Kim",
      isAdmin: false,
      date: "12/11/2024 4:30 PM",
      status: "draft" as const,
    },
    {
      id: "tr6",
      responderName: "Dr. Patricia Lee",
      isAdmin: true,
      date: "12/10/2024 2:00 PM",
      status: "completed" as const,
    },
  ],
  "4": [
    // Symptom Tracker Form
    {
      id: "s1",
      responderName: "Jennifer Davis",
      isAdmin: false,
      date: "12/05/2024 3:15 PM",
      status: "completed" as const,
    },
    {
      id: "s2",
      responderName: "Mark Wilson",
      isAdmin: false,
      date: "12/04/2024 10:30 AM",
      status: "completed" as const,
    },
    {
      id: "s3",
      responderName: "Dr. Susan Brown",
      isAdmin: true,
      date: "12/03/2024 2:45 PM",
      status: "completed" as const,
    },
  ],
};

// Mock individual response data (question-answer pairs)
const mockResponseData = {
  r1: [
    // Sarah Johnson's Patient Intake Form
    { question: "Full Name", answer: "Sarah Johnson" },
    { question: "Email Address", answer: "sarah.johnson@email.com" },
    { question: "Phone Number", answer: "(555) 123-4567" },
    { question: "Date of Birth", answer: "03/15/1985" },
    { question: "Emergency Contact Name", answer: "Michael Johnson" },
    { question: "Emergency Contact Phone", answer: "(555) 123-4568" },
    { question: "Preferred Contact Method", answer: "Email" },
    { question: "Do you have any allergies?", answer: "No known allergies" },
    { question: "Current medications", answer: "None" },
    { question: "Reason for visit", answer: "Annual checkup" },
  ],
  r2: [
    // Michael Chen's Patient Intake Form
    { question: "Full Name", answer: "Michael Chen" },
    { question: "Email Address", answer: "m.chen@email.com" },
    { question: "Phone Number", answer: "(555) 234-5678" },
    { question: "Date of Birth", answer: "07/22/1978" },
    { question: "Emergency Contact Name", answer: "Lisa Chen" },
    { question: "Emergency Contact Phone", answer: "(555) 234-5679" },
    { question: "Preferred Contact Method", answer: "Phone" },
    { question: "Do you have any allergies?", answer: "Penicillin" },
    { question: "Current medications", answer: "Lisinopril 10mg daily" },
    { question: "Reason for visit", answer: "Follow-up for hypertension" },
  ],
  r3: [
    // Dr. Amanda Smith's Admin Entry
    { question: "Patient Name", answer: "John Doe" },
    {
      question: "Admin Notes",
      answer: "Patient intake completed via phone interview",
    },
    { question: "Verification Status", answer: "Insurance verified" },
    {
      question: "Special Instructions",
      answer: "Patient requires interpreter services",
    },
    { question: "Priority Level", answer: "Standard" },
  ],
  r4: [
    // Emily Rodriguez's Draft
    { question: "Full Name", answer: "Emily Rodriguez" },
    { question: "Email Address", answer: "emily.r@email.com" },
    { question: "Phone Number", answer: "" },
    { question: "Date of Birth", answer: "" },
    { question: "Emergency Contact Name", answer: "" },
    { question: "Emergency Contact Phone", answer: "" },
  ],
  r5: [
    // Robert Williams's Health Assessment
    { question: "How would you rate your overall health?", answer: "Good" },
    {
      question: "Do you exercise regularly?",
      answer: "Yes, 3-4 times per week",
    },
    { question: "Do you smoke?", answer: "No" },
    {
      question: "How many hours of sleep do you get per night?",
      answer: "7-8 hours",
    },
    { question: "Stress level (1-10)", answer: "4" },
    {
      question: "Any concerns about your health?",
      answer: "Occasional back pain",
    },
  ],
  r6: [
    // Lisa Anderson's Health Assessment
    {
      question: "How would you rate your overall health?",
      answer: "Very Good",
    },
    { question: "Do you exercise regularly?", answer: "Yes, daily walking" },
    { question: "Do you smoke?", answer: "No" },
    {
      question: "How many hours of sleep do you get per night?",
      answer: "6-7 hours",
    },
    { question: "Stress level (1-10)", answer: "6" },
    {
      question: "Any concerns about your health?",
      answer: "None at this time",
    },
  ],
  // Timeline form responses data
  tr1: [
    // Maria Rodriguez's Patient Intake Form
    { question: "Full Name", answer: "Maria Rodriguez" },
    { question: "Email Address", answer: "maria.rodriguez@email.com" },
    { question: "Phone Number", answer: "(555) 987-6543" },
    { question: "Date of Birth", answer: "03/20/1992" },
    { question: "Emergency Contact Name", answer: "Carlos Rodriguez" },
    { question: "Emergency Contact Phone", answer: "(555) 987-6544" },
    { question: "Preferred Contact Method", answer: "SMS" },
    { question: "Do you have any allergies?", answer: "Shellfish allergy" },
    { question: "Current medications", answer: "Birth control" },
    { question: "Reason for visit", answer: "Follow-up consultation" },
  ],
  tr2: [
    // Dr. James Wilson's Admin Entry
    { question: "Patient Name", answer: "Robert Johnson" },
    { question: "Admin Notes", answer: "Patient intake reviewed and approved by attending physician" },
    { question: "Verification Status", answer: "Insurance and ID verified" },
    {
      question: "Special Instructions",
      answer: "Patient has mobility restrictions - wheelchair accessible room required",
    },
    { question: "Priority Level", answer: "High" },
    { question: "Follow-up Required", answer: "Yes - 2 week follow-up scheduled" },
  ],
  tr3: [
    // Alex Thompson's Patient Intake Form
    { question: "Full Name", answer: "Alex Thompson" },
    { question: "Email Address", answer: "alex.thompson@email.com" },
    { question: "Phone Number", answer: "(555) 456-7890" },
    { question: "Date of Birth", answer: "11/08/1988" },
    { question: "Emergency Contact Name", answer: "Emma Thompson" },
    { question: "Emergency Contact Phone", answer: "(555) 456-7891" },
    { question: "Preferred Contact Method", answer: "Email" },
    { question: "Do you have any allergies?", answer: "Peanuts, latex" },
    { question: "Current medications", answer: "Metformin 500mg twice daily" },
    { question: "Reason for visit", answer: "Diabetes management checkup" },
  ],
  tr4: [
    // Nurse Sarah Davis's Assessment
    { question: "Patient Assessment", answer: "Vitals stable, patient alert and responsive" },
    { question: "Blood Pressure", answer: "120/80 mmHg" },
    { question: "Heart Rate", answer: "72 bpm" },
    { question: "Temperature", answer: "98.6°F" },
    { question: "Weight", answer: "165 lbs" },
    { question: "Height", answer: "5'7\"" },
    { question: "Pain Level (0-10)", answer: "2" },
    { question: "Nursing Notes", answer: "Patient comfortable, no acute distress noted" },
  ],
  tr5: [
    // Jessica Kim's Draft (incomplete)
    { question: "Full Name", answer: "Jessica Kim" },
    { question: "Email Address", answer: "jessica.kim@email.com" },
    { question: "Phone Number", answer: "(555) 321-0987" },
    { question: "Date of Birth", answer: "" },
    { question: "Emergency Contact Name", answer: "" },
    { question: "Emergency Contact Phone", answer: "" },
    { question: "Preferred Contact Method", answer: "Phone" },
  ],
  tr6: [
    // Dr. Patricia Lee's Clinical Review
    { question: "Clinical Assessment", answer: "Comprehensive review completed" },
    { question: "Diagnosis", answer: "Essential hypertension, well-controlled" },
    { question: "Treatment Plan", answer: "Continue current medication regimen" },
    { question: "Recommendations", answer: "Maintain low-sodium diet, regular exercise" },
    { question: "Next Appointment", answer: "3 months" },
    { question: "Lab Orders", answer: "CBC, BMP, lipid panel in 6 weeks" },
    { question: "Provider Notes", answer: "Patient compliant with treatment, excellent progress" },
  ],
  // Symptom Tracker Form responses
  s1: [
    // Jennifer Davis's Symptom Tracker
    { question: "Today's Date", answer: "12/05/2024" },
    { question: "Overall Energy Level (1-10)", answer: "6" },
    { question: "Sleep Quality (1-10)", answer: "7" },
    { question: "Pain Level (1-10)", answer: "3" },
    { question: "Mood (1-10)", answer: "8" },
    { question: "Appetite", answer: "Good" },
    { question: "Primary Symptoms Today", answer: "Mild headache, some fatigue in afternoon" },
    { question: "Medication Taken", answer: "Tylenol 500mg at 2pm" },
    { question: "Physical Activity", answer: "30 min walk in the morning" },
    { question: "Additional Notes", answer: "Feeling much better than yesterday. Headache is manageable." },
  ],
  s2: [
    // Mark Wilson's Symptom Tracker
    { question: "Today's Date", answer: "12/04/2024" },
    { question: "Overall Energy Level (1-10)", answer: "4" },
    { question: "Sleep Quality (1-10)", answer: "5" },
    { question: "Pain Level (1-10)", answer: "7" },
    { question: "Mood (1-10)", answer: "6" },
    { question: "Appetite", answer: "Poor" },
    { question: "Primary Symptoms Today", answer: "Persistent back pain, difficulty concentrating" },
    { question: "Medication Taken", answer: "Ibuprofen 400mg twice daily" },
    { question: "Physical Activity", answer: "Light stretching only" },
    { question: "Additional Notes", answer: "Back pain worse today. May need to adjust treatment plan." },
  ],
  s3: [
    // Dr. Susan Brown's Clinical Assessment
    { question: "Patient Name", answer: "Robert Martinez" },
    { question: "Assessment Date", answer: "12/03/2024" },
    { question: "Overall Condition", answer: "Stable with improvement noted" },
    { question: "Pain Management", answer: "Effective with current regimen" },
    { question: "Functional Status", answer: "Independent with daily activities" },
    { question: "Medication Compliance", answer: "Excellent" },
    {
      question: "Clinical Notes",
      answer:
        "Patient showing steady improvement. Pain levels decreased significantly over past week. Continue current treatment plan.",
    },
    { question: "Next Steps", answer: "Follow-up in 1 week, continue current medications" },
    { question: "Provider Signature", answer: "Dr. Susan Brown, MD" },
  ],
};

export interface ContactsDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultExpanded?: boolean;
  activeSection?: (typeof NAVIGATION_DATA.contacts)[number]["name"] | (typeof NAVIGATION_DATA.settings)[number]["name"];
  activeSubSection?: string; // TODO: add better typing here
  triggerButton?: ReactNode;
  showTrigger?: boolean;
  contactName?: string;
  content?: React.ReactNode;
  sectionCount?: number;
  contact?: {
    id?: string;
    fname?: string;
    lname?: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string | number;
    createdAt: string | number;
    updatedAt: string | number;
  };
  medications?: Array<{
    id: string;
    title: string;
    enduserId: string;
    dosage?: {
      value: string;
      unit: string;
      description?: string;
      quantity?: string;
      frequency?: string;
      frequencyDescriptor?: string;
    };
    reasonForTaking?: string;
    prescribedBy?: string;
    prescriberName?: string;
    pharmacyName?: string;
    orderStatus?: string;
    startedTakingAt?: Date | string;
    createdAt?: Date | string;
  }>;
  medicationsLoading?: boolean;
  onMedicationCreate?: (medicationData: {
    enduserId: string;
    title: string;
    dosage?: {
      value: string;
      unit: string;
      description?: string;
      quantity?: string;
      frequency?: string;
      frequencyDescriptor?: string;
    };
    reasonForTaking?: string;
    prescribedBy?: string;
    prescriberName?: string;
    orderStatus?: string;
  }) => Promise<void>;
  eligibilityResults?: EligibilityResult[];
  onEligibilityCreate?: (eligibilityData: { enduserId: string; title: string; type: string }) => Promise<void>;
  purchases: ClientModelForName["purchases"][];
  onPurchaseCreate: (newPurchase: CreateFields<"purchases">) => Promise<void>;
  // TODO: fix this type. The type from schema has weird stuff like validators
  onChargeCard: (params: {
    enduserId: string;
    productIds?: string[];
    priceIds?: string[];
    cost?: Purchase["cost"];
    stripeKey?: string;
    description?: string;
    subscriptionPriceId?: string;
  }) => Promise<void>;
  products: Product[];
}

function ContactsDialogComponent({
  open = false,
  onOpenChange,
  defaultExpanded = false,
  activeSection = "Information",
  activeSubSection,
  triggerButton,
  showTrigger = true,
  contactName = "Settings",
  content,
  sectionCount,
  contact,
  medications: _medications,
  medicationsLoading,
  onMedicationCreate: _onMedicationCreate,
  eligibilityResults = [],
  onEligibilityCreate,
  purchases,
  onPurchaseCreate,
  onChargeCard,
  products,
}: ContactsDialogProps) {
  const { t } = useTranslation("contacts");
  const [isOpen, setIsOpen] = useState(open);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [currentSection, setCurrentSection] = useState(activeSection);
  const [currentSubSection, setCurrentSubSection] = useState(activeSubSection);

  // State for managing submitted forms
  const [submittedForms, setSubmittedForms] = useState<
    Array<{
      id: string;
      title: string;
      responses: string;
      date: string;
    }>
  >([]);

  // State for managing selected form and its responses
  const [selectedForm, setSelectedForm] = useState<{
    id: string;
    title: string;
  } | null>(null);

  // State for managing selected individual response
  const [selectedResponse, setSelectedResponse] = useState<{
    id: string;
    responderName: string;
    isAdmin: boolean;
    date: string;
    status?: "completed" | "draft";
  } | null>(null);

  // State for managing form response viewer
  const [showResponseViewer, setShowResponseViewer] = useState(false);

  const [currentlyEditing, setCurrentlyEditing] = useState<{
    type: "note" | "form";
    id: string;
    title?: string;
    date?: string;
  } | null>(null);

  const handleStopEditing = () => {
    setCurrentlyEditing(null);
  };

  // Handlers for form selection and navigation
  const handleFormSelect = (formId: string, formTitle: string) => {
    setSelectedForm({ id: formId, title: formTitle });
    setCurrentSubSection(formTitle);
  };

  const handleResponseSelect = (response: {
    id: string;
    responderName: string;
    isAdmin: boolean;
    date: string;
    status?: "completed" | "draft";
  }) => {
    setSelectedResponse(response);
    // Use response date for breadcrumb instead of responder name
    setCurrentSubSection(response.date);
  };

  // Function to create new eligibility request from form submission
  const addNewEligibilityRequest = async (formData: {
    testSubmissionType: string;
    provider: string;
    insuranceType: string;
    diagnosisCodes: string;
    currentlyTaking: string;
    previouslyTaken: string;
    selectedDrugs: Array<{
      name: string;
      dosage: string;
      quantity: string;
    }>;
  }) => {
    if (!onEligibilityCreate || !contact?.id) {
      console.warn("No eligibility creation handler or contact ID provided");
      return;
    }

    try {
      // Create eligibility request
      await onEligibilityCreate({
        enduserId: contact.id,
        title: `${formData.testSubmissionType} - ${formData.insuranceType}`,
        type: formData.testSubmissionType,
      });

      // Navigate back to eligibility list
      setCurrentSection("Eligibility");
      setCurrentSubSection(undefined);
    } catch (error) {
      console.error("Failed to create eligibility request:", error);
    }
  };

  // Function to handle form submission and add to forms list
  const addSubmittedForm = (formTitle: string, _responses: Record<string, string | boolean>) => {
    setSubmittedForms((prev) => {
      // Check if this form type already exists
      const existingFormIndex = prev.findIndex((form) => form.title === formTitle);

      if (existingFormIndex >= 0) {
        // Increment the response count for existing form
        const updatedForms = [...prev];
        const existingForm = updatedForms[existingFormIndex];
        if (!existingForm) return prev;
        const currentCount = parseInt(existingForm.responses);
        updatedForms[existingFormIndex] = {
          ...existingForm,
          responses: String(currentCount + 1),
          date: new Date().toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          }),
        };
        return updatedForms;
      } else {
        // Create new form entry with 1 response
        const newForm = {
          id: String(Date.now()),
          title: formTitle,
          responses: "1",
          date: new Date().toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          }),
        };
        return [newForm, ...prev];
      }
    });

    // Navigate back to main Forms section
    setCurrentSection("Forms");
    setCurrentSubSection(undefined);
  };

  // Function to handle send to patient navigation
  const handleSendToPatient = () => {
    // Navigate back to main Forms section
    setCurrentSection("Forms");
    setCurrentSubSection(undefined);
  };

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    setCurrentSection(activeSection);
  }, [activeSection]);

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    // Reset expanded state when dialog is closed
    if (!newOpen) {
      setIsExpanded(false);
    }
    onOpenChange?.(newOpen);
  };

  const navItems = contactName === "Settings" ? NAVIGATION_DATA.settings : NAVIGATION_DATA.contacts;

  // Function to render section content
  const renderSectionContent = () => {
    if (content) {
      return content;
    }

    switch (currentSection) {
      case "Information":
        return <InformationContent contact={contact} />;
      case "Timeline":
        return (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <div className="text-center space-y-4 p-8">
              <div className="text-lg font-medium text-muted-foreground">{t("Timeline item designs in Storybook")}</div>
              <div className="text-sm text-muted-foreground max-w-md">
                {t("Timeline components are currently available in Storybook for design review and development.")}
              </div>
            </div>
          </div>
        );
      case "Tickets":
        return <TicketsContent />;
      case "Events":
        return <EventsContent />;
      case "Eligibility":
        if (currentSubSection === "Submit eligibility check") {
          return <EligibilitySubmissionContent onSubmit={addNewEligibilityRequest} />;
        }
        return <EligibilityContent eligibilityResults={eligibilityResults} />;
      case "Forms":
        if (currentSubSection === "Select form template") {
          return (
            <FormTemplateSelectionContent onFormSelect={(formTitle) => setCurrentSubSection(`Form: ${formTitle}`)} />
          );
        }
        if (currentSubSection && currentSubSection.startsWith("Form: ")) {
          return (
            <FormQuestionnaireContent
              formTitle={currentSubSection.replace("Form: ", "")}
              onFormSubmit={addSubmittedForm}
              onSendToPatient={handleSendToPatient}
            />
          );
        }
        if (showResponseViewer && selectedForm) {
          return (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center space-y-4">
                <div className="text-lg font-medium text-muted-foreground">{t("Form Response Viewer")}</div>
                <div className="text-sm text-muted-foreground">{t("This component has been removed")}</div>
              </div>
            </div>
          );
        }
        if (selectedResponse) {
          return (
            <ResponseDetailContent
              response={selectedResponse!}
              responseData={
                selectedResponse ? mockResponseData[selectedResponse.id as keyof typeof mockResponseData] || [] : []
              }
            />
          );
        }
        if (selectedForm) {
          return (
            <FormResponsesContent
              responses={selectedForm ? mockFormResponses[selectedForm.id as keyof typeof mockFormResponses] || [] : []}
              onResponseSelect={handleResponseSelect}
            />
          );
        }
        return <FormsContent forms={[...submittedForms, ...initialForms]} onFormSelect={handleFormSelect} />;
      case "Files":
        if (currentSubSection === "Upload file") {
          return <FileUploadContent onSubmit={() => setCurrentSubSection(undefined)} />;
        }
        return <FilesContent />;
      case "Medications":
        if (currentSubSection === "Upload medications") {
          return <MedicationUpload onSubmit={() => setCurrentSubSection(undefined)} />;
        }
        return <MedicationsContent medications={_medications} isLoading={medicationsLoading} enduserId={contact?.id} />;
      case "Payment":
        switch (currentSubSection) {
          case "Log purchase":
            return (
              <LogPurchaseContent
                enduserId={contact?.id ?? ""}
                products={products}
                onSubmit={async (newPurchase) => {
                  await onPurchaseCreate(newPurchase);
                  setCurrentSubSection(undefined);
                }}
                onCancel={() => setCurrentSubSection(undefined)}
              />
            );
          case "Charge payment":
            return (
              <ChargePurchaseContent
                enduserId={contact?.id ?? ""}
                products={products}
                onSubmit={async (chargeArgs) => {
                  await onChargeCard(chargeArgs);
                  setCurrentSubSection(undefined);
                }}
                onCancel={() => {
                  setCurrentSubSection(undefined);
                }}
              />
            );
          default:
            return <PaymentContent purchases={purchases} />;
        }
      default:
        return (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center space-y-4">
              <div className="text-lg font-medium text-muted-foreground">{currentSection}</div>
              <div className="text-sm text-muted-foreground">This component hasn't been built yet</div>
            </div>
          </div>
        );
    }
  };

  const dialogContent = (
    <div className={cn("relative w-full", isExpanded ? "h-full flex flex-col" : "h-full")}>
      <DialogTitle className="sr-only">{contactName}</DialogTitle>
      <DialogDescription className="sr-only">
        {contactName === "Settings" ? "Customize your settings here." : "View and edit contact information."}
      </DialogDescription>
      <SidebarProvider className={isExpanded ? "items-start w-full h-full" : "items-start"}>
        <Sidebar
          collapsible="none"
          className={isExpanded ? "hidden md:flex flex-shrink-0 w-[240px] h-full" : "hidden md:flex w-[200px] h-full"}
        >
          <SidebarContent className="flex-1 flex flex-col">
            <SidebarGroup className="flex-1">
              <SidebarGroupContent className="flex-1">
                <SidebarMenu>
                  {navItems.map((item) => (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton
                        isActive={item.name === currentSection}
                        onClick={() => setCurrentSection(item.name)}
                      >
                        <item.icon />
                        <span>{item.name}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>
        <main
          className={
            isExpanded
              ? "flex h-full flex-1 flex-col overflow-hidden w-full min-w-0"
              : "flex h-[480px] flex-1 flex-col overflow-hidden"
          }
        >
          <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
            <div className="flex items-center gap-2 px-4">
              <Button variant="ghost" onClick={() => setIsExpanded(!isExpanded)} className="size-6 p-0">
                {isExpanded ? <Minimize className="h-4 w-4" /> : <Fullscreen className="h-4 w-4" />}
              </Button>
              <Breadcrumb className="flex-1 min-w-0">
                <BreadcrumbList className="overflow-x-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent flex-nowrap">
                  <BreadcrumbItem className="hidden md:block flex-shrink-0">
                    <BreadcrumbLink
                      onClick={(e) => e.preventDefault()}
                      title={contactName}
                      className="cursor-default max-w-[150px] truncate"
                    >
                      {contactName}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="hidden md:block flex-shrink-0" />
                  <BreadcrumbItem className="flex-shrink-0">
                    {currentSubSection && currentSection === "Forms" ? (
                      <BreadcrumbLink
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentSubSection(undefined);
                          setSelectedForm(null);
                          setSelectedResponse(null);
                          setShowResponseViewer(false);
                        }}
                        className="flex items-center gap-4 cursor-pointer"
                      >
                        {currentSection}
                        {sectionCount !== undefined && (
                          <span className="text-sm text-muted-foreground">
                            {t("({{amount}} total)", { amount: sectionCount })}
                          </span>
                        )}
                      </BreadcrumbLink>
                    ) : currentSubSection || selectedForm ? (
                      <BreadcrumbLink
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentSubSection(undefined);
                          setSelectedForm(null);
                          setSelectedResponse(null);
                          setShowResponseViewer(false);
                        }}
                        className="flex items-center gap-4 cursor-pointer"
                      >
                        {currentSection}
                        {(sectionCount !== undefined || (currentSection === "Eligibility" && eligibilityResults)) && (
                          <span className="text-sm text-muted-foreground">
                            {t("({{amount}} total)", {
                              amount: currentSection === "Eligibility" ? eligibilityResults.length : sectionCount,
                            })}
                          </span>
                        )}
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage className="flex items-center gap-4">
                        {currentSection}
                        {(sectionCount !== undefined || (currentSection === "Eligibility" && eligibilityResults)) && (
                          <span className="text-sm text-muted-foreground">
                            {t("({{amount}} total)", {
                              amount: currentSection === "Eligibility" ? eligibilityResults.length : sectionCount,
                            })}
                          </span>
                        )}
                        {currentSection === "Eligibility" && !currentSubSection && (
                          <button
                            onClick={() => setCurrentSubSection("Submit eligibility check")}
                            className="flex items-center justify-center rounded-md size-6 hover:bg-muted transition-colors"
                          >
                            <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                          </button>
                        )}
                        {currentSection === "Forms" && !selectedForm && !currentSubSection && (
                          <button
                            onClick={() => setCurrentSubSection("Select form template")}
                            className="flex items-center justify-center rounded-md size-6 hover:bg-muted transition-colors"
                          >
                            <ListPlus className="h-4 w-4 text-muted-foreground" />
                          </button>
                        )}

                        {currentSection === "Files" && !currentSubSection && (
                          <button
                            onClick={() => setCurrentSubSection("Upload file")}
                            className="flex items-center justify-center rounded-md size-6 hover:bg-muted transition-colors"
                          >
                            <FilePlus2 className="h-4 w-4 text-muted-foreground" />
                          </button>
                        )}

                        {currentSection === "Medications" && !currentSubSection && (
                          <button
                            onClick={() => setCurrentSubSection("Upload medications")}
                            className="flex items-center justify-center rounded-md size-6 hover:bg-muted transition-colors"
                          >
                            <CloudUpload className="h-4 w-4 text-muted-foreground" />
                          </button>
                        )}
                        {currentSection === "Payment" && !currentSubSection && (
                          <div className="flex items-center justify-center">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => setCurrentSubSection("Log purchase")}
                                    className="flex items-center justify-center rounded-md size-6 hover:bg-muted transition-colors"
                                  >
                                    <Banknote className="h-4 w-4 text-muted-foreground" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{t("Log Purchase")}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => setCurrentSubSection("Charge payment")}
                                    className="flex items-center justify-center rounded-md size-6 hover:bg-muted transition-colors"
                                  >
                                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{t("Charge")}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        )}
                      </BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                  {currentSubSection && currentSection === "Forms" && selectedForm && (
                    <>
                      <BreadcrumbSeparator className="flex-shrink-0" />
                      <BreadcrumbItem className="flex-shrink-0">
                        {selectedResponse ? (
                          <BreadcrumbLink
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedResponse(null);
                              setCurrentSubSection(selectedForm.title);
                            }}
                            className="cursor-pointer max-w-[200px] truncate"
                            title={selectedForm.title}
                          >
                            {selectedForm.title}
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage className="max-w-[200px] truncate" title={selectedForm.title}>
                            {selectedForm.title}
                          </BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </>
                  )}
                  {currentSubSection && currentSection === "Forms" && selectedResponse && (
                    <>
                      <BreadcrumbSeparator className="flex-shrink-0" />
                      <BreadcrumbItem className="flex-shrink-0">
                        <BreadcrumbPage className="max-w-[150px] truncate" title={selectedResponse.date}>
                          {selectedResponse.date}
                        </BreadcrumbPage>
                      </BreadcrumbItem>
                    </>
                  )}
                  {currentSubSection && currentSection !== "Forms" && (
                    <>
                      <BreadcrumbSeparator className="flex-shrink-0" />
                      <BreadcrumbItem className="flex-shrink-0">
                        <BreadcrumbPage className="max-w-[200px] truncate" title={currentSubSection}>
                          {currentSubSection}
                        </BreadcrumbPage>
                      </BreadcrumbItem>
                    </>
                  )}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </header>
          <div className="flex flex-1 flex-col overflow-hidden px-4 pt-0 pb-0">{renderSectionContent()}</div>
        </main>
      </SidebarProvider>

      {/* Global editing footer */}
      {currentlyEditing && (
        <div
          className="fixed bottom-4 left-4 right-4 bg-white border border-gray-200 px-6 py-4 z-[9999] shadow-2xl rounded-lg"
          style={{ zIndex: 9999 }}
        >
          <div className="flex items-center justify-between max-w-full">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900">
                {currentlyEditing.type === "note" ? t("Editing Note") : t("Editing Form")}
              </div>
              <div className="text-sm text-gray-600 truncate">
                {currentlyEditing.title || `${currentlyEditing.type} ${currentlyEditing.id}`}
              </div>
              {currentlyEditing.date && <div className="text-xs text-gray-500">{currentlyEditing.date}</div>}
            </div>

            <div className="flex items-center space-x-2 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={handleStopEditing}>
                {t("Cancel")}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  console.log("Saving changes for:", currentlyEditing);
                  handleStopEditing();
                }}
              >
                {t("Save Changes")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isExpanded && isOpen) {
    return (
      <>
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
          {showTrigger && (
            <DialogTrigger asChild>{triggerButton || <Button size="sm">{t("Open Dialog")}</Button>}</DialogTrigger>
          )}
          <DialogContent
            className="p-0 m-0 max-w-[calc(100%-16px)] max-h-[calc(100%-16px)] w-[calc(100%-16px)] h-[calc(100%-16px)] flex flex-col overflow-hidden"
            style={{
              width: "calc(100% - 16px)",
              height: "calc(100% - 16px)",
              maxWidth: "calc(100% - 16px)",
              maxHeight: "calc(100% - 16px)",
              display: "flex",
            }}
          >
            {dialogContent}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {showTrigger && (
        <DialogTrigger asChild>{triggerButton || <Button size="sm">{t("Open Dialog")}</Button>}</DialogTrigger>
      )}
      <DialogContent className="overflow-hidden p-0 md:max-h-[500px] md:max-w-[700px] lg:max-w-[800px]">
        {dialogContent}
      </DialogContent>
    </Dialog>
  );
}

/** @deprecated use the `ContactsDialog` in the web app or separate our your own component */
export const ContactsDialog = Object.assign(ContactsDialogComponent, {
  EligibilityContent,
  EligibilitySubmissionContent,
  EventsContent,
  FileUploadContent,
  FilesContent,
  FormQuestionnaireContent,
  FormResponsesContent,
  FormTemplateSelectionContent,
  FormsContent,
  InformationContent,
  MedicationsContent,
  MedicationUpload,
  ResponseDetailContent,
  TicketsContent,
  PaymentContent,
});
